/**
 * Minimal, dependency-free CSV parsing for requirement import.
 *
 * Handles the common cases produced by Excel / Google Sheets exports:
 * - comma-separated fields
 * - double-quoted fields containing commas, newlines, or escaped quotes ("")
 * - CRLF or LF line endings
 *
 * It is intentionally small — not a full RFC-4180 implementation — but covers
 * what requirement import needs: turning a CSV file into a flat list of
 * requirement text strings.
 */

/** Parses CSV text into a matrix of rows × cells. */
export function parseCsv(input: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < input.length; i++) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"'; // escaped quote
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\r') {
      // Handle both CRLF and CR-only line endings.
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      if (input[i + 1] === '\n') i++; // consume the \n of CRLF
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  // Flush the final field/row if the file does not end with a newline.
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
}

/** Header names (case-insensitive) that identify the requirement-text column.
 * 'title' is included because the UI hint shows "title column or first column"
 * as the supported format.
 */
const REQUIREMENT_HEADERS = [
  'requirement',
  'requirements',
  'text',
  'title',
  '要件',
  '要求',
];

/**
 * Returns true when the CSV's first row contains at least one recognisable
 * requirement column header.  Used to surface a friendly error when the user
 * uploads a CSV without the expected header row.
 */
export function hasCsvHeader(csvContent: string): boolean {
  const content = csvContent.replace(/^\uFEFF/, '');
  const rows = parseCsv(content).filter(r => r.some(c => c.trim().length > 0));
  if (rows.length === 0) return false;
  const header = rows[0].map(c => c.trim().toLowerCase());
  return header.some(h => REQUIREMENT_HEADERS.includes(h));
}

/**
 * Extracts a flat list of requirement texts from raw CSV content.
 *
 * Strategy:
 * - Parse into rows.
 * - If the first row looks like a header containing a known requirement column,
 *   take that column from every subsequent row.
 * - Otherwise, treat the first column of every row as the requirement text.
 * Empty cells are dropped.
 */
export function extractRequirementTexts(csvContent: string): string[] {
  // Strip UTF-8 BOM that Excel/Windows tools sometimes prepend.
  const content = csvContent.replace(/^\uFEFF/, '');
  const rows = parseCsv(content).filter(r => r.some(c => c.trim().length > 0));
  if (rows.length === 0) {
    return [];
  }

  const header = rows[0].map(c => c.trim().toLowerCase());
  const reqColIndex = header.findIndex(h => REQUIREMENT_HEADERS.includes(h));

  if (reqColIndex !== -1) {
    // Skip the header row, pull the identified column.
    return rows
      .slice(1)
      .map(r => (r[reqColIndex] ?? '').trim())
      .filter(text => text.length > 0);
  }

  // No recognizable header — treat every row as data and use the first column.
  return rows.map(r => (r[0] ?? '').trim()).filter(text => text.length > 0);
}
